import { FRAME_OPTIONS, formatUsd, priceForCents } from '@lumina/shared';
import { pairStatus, type Assignments, type CatalogSize } from './PrintSizeAssignment';

/** Below this share of the price, a margin is flagged as thin. */
const THIN_MARGIN = 0.25;

/** What the customer pays for one print of a photo at `size`, before any frame. */
export function printPriceCents(basePriceCents: number, size: Pick<CatalogSize, 'multiplier'>) {
  return priceForCents({ priceBaseCents: basePriceCents }, size, 'none');
}

function marginColor(margin: number, price: number) {
  if (margin <= 0) return '#b91c1c';
  if (margin / price < THIN_MARGIN) return '#b45309';
  return '#15803d';
}

/**
 * Price, Gelato cost and margin for every size × frame this photo is assigned,
 * computed the same way checkout does (base × size multiplier + frame).
 */
export function PhotoPricing({
  photo,
  sizes,
  assignments,
}: {
  photo: { width: number; height: number; price_base_cents: number };
  sizes: CatalogSize[];
  assignments: Assignments;
}) {
  const assigned = sizes.filter((s) => assignments[s.id]);
  const base = photo.price_base_cents;

  return (
    <div style={{ margin: '4px 0 16px', padding: 12, background: '#fafafa', border: '1px solid #eee', borderRadius: 6 }}>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
        What a customer pays for this photo, what Gelato charges us, and what's left. Price = base{' '}
        {formatUsd(base)} × the size's multiplier + the frame. Shipping is charged separately; Stripe
        fees (~3%) aren't deducted.
      </div>
      {assigned.length === 0 ? (
        <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>No sizes assigned.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12, background: '#fff' }}>
            <thead>
              <tr>
                <th style={th}>Size</th>
                {FRAME_OPTIONS.map((f) => (
                  <th key={f.id} style={th}>
                    {f.label}
                    <div style={{ fontWeight: 400, fontSize: 10, color: '#6b7280' }}>
                      {f.priceCents === 0 ? 'no add-on' : `+${formatUsd(f.priceCents)}`}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assigned.map((s) => {
                const status = pairStatus(photo, s, assignments[s.id]);
                return (
                  <tr key={s.id} style={{ borderTop: '1px solid #f0f0f0', opacity: status.offered ? 1 : 0.55 }}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 500 }}>{s.label}</div>
                      <div style={{ fontSize: 10, color: '#6b7280' }}>× {s.multiplier}</div>
                      {!status.offered && (
                        <div style={{ fontSize: 10, color: '#b45309' }}>Not offered: {status.reason}</div>
                      )}
                    </td>
                    {FRAME_OPTIONS.map((f) => {
                      const product = s.products[f.id];
                      if (!product) {
                        return (
                          <td key={f.id} style={{ ...td, color: '#9ca3af' }}>
                            not sold
                          </td>
                        );
                      }
                      const price = priceForCents({ priceBaseCents: base }, s, f.id);
                      const cost = product.cost_cents;
                      const margin = cost == null ? null : price - cost;
                      return (
                        <td key={f.id} style={{ ...td, whiteSpace: 'nowrap' }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{formatUsd(price)}</div>
                          {cost == null || margin == null ? (
                            <div style={{ fontSize: 10, color: '#b45309' }}>cost unknown</div>
                          ) : (
                            <>
                              <div style={{ fontSize: 10, color: '#6b7280' }}>cost {formatUsd(cost)}</div>
                              <div style={{ fontSize: 10, color: marginColor(margin, price) }}>
                                keep {formatUsd(margin)}
                                {price > 0 && ` (${Math.round((margin / price) * 100)}%)`}
                              </div>
                            </>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  fontSize: 11,
  fontWeight: 600,
  color: '#374151',
  borderBottom: '1px solid #e5e5e5',
  verticalAlign: 'bottom',
};

const td: React.CSSProperties = { padding: '6px 8px', verticalAlign: 'top' };
